/**
 * @param {string} hourStr
 * @returns {string} Two-digit hour, or "00" if out of range
 */
const normalizeHourPart = (hourStr) => {
  const hour = Number.parseInt(hourStr, 10);
  if (Number.isNaN(hour) || hour > 23) {
    return '00';
  }
  return String(hour).padStart(2, '0');
};

/**
 * @param {string} minuteStr
 * @returns {string} Two-digit minutes, or "00" if out of range
 */
const normalizeMinutePart = (minuteStr) => {
  if (!minuteStr) {
    return '00';
  }

  if (minuteStr.length === 1) {
    const digit = Number.parseInt(minuteStr, 10);
    if (Number.isNaN(digit)) {
      return '00';
    }
    if (digit <= 5) {
      return `${digit}0`;
    }
    return `0${digit}`;
  }

  const minutes = Number.parseInt(minuteStr.slice(0, 2), 10);
  if (Number.isNaN(minutes) || minutes > 59) {
    return '00';
  }
  return String(minutes).padStart(2, '0');
};

/**
 * @param {string} digits
 * @returns {{ hourStr: string, minuteStr: string }}
 */
const parseSingleDigitGroup = (digits) => {
  if (digits.length === 1) {
    return { hourStr: digits, minuteStr: '00' };
  }

  if (digits.length === 2) {
    return { hourStr: digits, minuteStr: '00' };
  }

  if (digits.length === 3) {
    const firstTwo = digits.slice(0, 2);
    const firstTwoHour = Number.parseInt(firstTwo, 10);
    if (!Number.isNaN(firstTwoHour) && firstTwoHour <= 23) {
      return { hourStr: firstTwo, minuteStr: digits.slice(2) };
    }
    return { hourStr: digits.slice(0, 1), minuteStr: digits.slice(1) };
  }

  return {
    hourStr: digits.slice(0, 2),
    minuteStr: digits.slice(2, 4),
  };
};

/**
 * Parse free-form user input into HH:mm on commit.
 * @param {string} raw - Raw input string
 * @returns {string | null} HH:mm, empty string to clear, or null when unparseable (no digits)
 */
export const parseTimeInput = (raw) => {
  if (raw === null || raw === undefined) {
    return '';
  }

  const trimmed = String(raw).trim();
  if (!trimmed) {
    return '';
  }

  const digitGroups = trimmed.match(/\d+/g);
  if (!digitGroups || digitGroups.length === 0) {
    return null;
  }

  let hourStr;
  let minuteStr;

  if (digitGroups.length >= 2) {
    [hourStr, minuteStr] = digitGroups;
  } else {
    const parsed = parseSingleDigitGroup(digitGroups[0]);
    hourStr = parsed.hourStr;
    minuteStr = parsed.minuteStr;
  }

  const hours = normalizeHourPart(hourStr);
  const minutes = normalizeMinutePart(minuteStr);

  return `${hours}:${minutes}`;
};

/**
 * All quarter-hour options from 00:00 to 23:45
 * @returns {string[]} List of HH:mm values
 */
export const getQuarterHourOptions = () => Array.from({ length: 96 }, (_, index) => {
  const hour = Math.floor(index / 4);
  const minute = (index % 4) * 15;
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
});
